import { useState, useEffect, useCallback, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { IoSearch, IoClose, IoPeople, IoBusiness, IoPerson, IoShieldCheckmark, IoSwapHorizontal } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/ActionItemsStyles.scss';
import * as Sentry from '@sentry/react';
import { FaIcon } from '@/utils/faIconMap';

/**
 * ManageAccessModal Component
 * Allows board owners to grant and revoke access to users, departments, and roles.
 * Uses a tag-based searchable multi-select pattern matching the Board Creation Rules UI.
 *
 * @param {Object} props Component props
 * @param {Object} props.board - The board object
 * @param {Array} props.sharedWith - Array of current user access grants
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onGrantAccess - Callback to grant user access
 * @param {Function} props.onRevokeAccess - Callback to revoke user access
 */
const ManageAccessModal = ({ board, sharedWith = [], onClose, onGrantAccess, onRevokeAccess, onAccessChanged }) => {
    const { apiFetch, user } = useAuth();

    // Lookup data
    const [allUsers, setAllUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);

    // Grant data from backend
    const [departmentGrants, setDepartmentGrants] = useState([]);
    const [roleGrants, setRoleGrants] = useState([]);

    // Search state
    const [roleSearch, setRoleSearch] = useState('');
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const [deptSearch, setDeptSearch] = useState('');
    const [showDeptDropdown, setShowDeptDropdown] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);

    // Owner transfer state
    const [ownerSearch, setOwnerSearch] = useState('');
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
    const [transferring, setTransferring] = useState(false);
    const [currentOwnerId, setCurrentOwnerId] = useState(board.ownerId);
    const [currentOwnerName, setCurrentOwnerName] = useState(board.ownerName || 'Unknown');
    const ownerSearchRef = useRef(null);

    // Permission level for new grants
    const [rolePermission, setRolePermission] = useState('Edit');
    const [deptPermission, setDeptPermission] = useState('Edit');
    const [userPermission, setUserPermission] = useState('Edit');

    // Refs for click-outside
    const roleSearchRef = useRef(null);
    const deptSearchRef = useRef(null);
    const userSearchRef = useRef(null);

    const PERMISSION_OPTIONS = [
        { value: 'View', label: 'View' },
        { value: 'Edit', label: 'Edit' },
        { value: 'Admin', label: 'Admin' }
    ];

    // Inline styles matching BoardCreationRulesPage pattern
    const styles = {
        formField: { marginBottom: '16px' },
        label: { display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px' },
        helpText: { display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '6px' },
        searchableSelect: { position: 'relative', marginTop: '6px' },
        searchInput: { width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' },
        searchInputIcon: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' },
        dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '2px', maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
        dropdownItem: { padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.1s' },
        dropdownEmpty: { padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' },
        selectedTags: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
        tag: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500 },
        tagRole: { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
        tagDept: { background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24' },
        tagUser: { background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' },
        tagRemove: { background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', display: 'inline-flex', alignItems: 'center', color: 'inherit', opacity: 0.7 },
        permBadge: { fontSize: '0.65rem', padding: '1px 6px', borderRadius: '8px', background: 'rgba(0,0,0,0.08)', fontWeight: 600, marginLeft: '2px' },
        permSelect: { padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', marginLeft: '8px' },
        sectionDivider: { borderTop: '1px solid #e5e7eb', marginTop: '20px', paddingTop: '16px' },
        boardHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '20px' },
        boardName: { margin: 0, fontSize: '1.1rem', fontWeight: 600 },
        boardDesc: { margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' },
    };

    // ── Data fetching ──────────────────────────────────────────────────────
    const fetchUsers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/view');
            const data = await response.json();
            if (data.success) setAllUsers(data.data || []);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to fetch users:', err);
        }
    }, [apiFetch]);

    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            if (data.success) setDepartments(data.data || []);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to fetch departments:', err);
        }
    }, [apiFetch]);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/roles');
            const data = await response.json();
            if (data.success) setRoles(data.data || []);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to fetch roles:', err);
        }
    }, [apiFetch]);

    const fetchDepartmentGrants = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/department-access`);
            const data = await response.json();
            if (data.success) setDepartmentGrants(data.data || []);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to fetch department grants:', err);
        }
    }, [apiFetch, board.boardId]);

    const fetchRoleGrants = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/role-access`);
            const data = await response.json();
            if (data.success) setRoleGrants(data.data || []);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to fetch role grants:', err);
        }
    }, [apiFetch, board.boardId]);

    useEffect(() => {
        (async () => {
            await Promise.all([fetchUsers(), fetchDepartments(), fetchRoles(), fetchDepartmentGrants(), fetchRoleGrants()]);
        })();
    }, [fetchUsers, fetchDepartments, fetchRoles, fetchDepartmentGrants, fetchRoleGrants]);

    // Click-outside to close dropdowns
    useEffect(() => {
        const handleClick = (e) => {
            if (roleSearchRef.current && !roleSearchRef.current.contains(e.target)) setShowRoleDropdown(false);
            if (deptSearchRef.current && !deptSearchRef.current.contains(e.target)) setShowDeptDropdown(false);
            if (userSearchRef.current && !userSearchRef.current.contains(e.target)) setShowUserDropdown(false);
            if (ownerSearchRef.current && !ownerSearchRef.current.contains(e.target)) setShowOwnerDropdown(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // ── Grant / Revoke handlers ────────────────────────────────────────────
    const handleGrantRole = async (roleId) => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/role-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleId, permissionLevel: rolePermission })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Role access granted');
                setRoleSearch('');
                setShowRoleDropdown(false);
                fetchRoleGrants();
                onAccessChanged?.();
            } else {
                toast.error(data.message || 'Failed to grant role access');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to grant role access');
        }
    };

    const handleRevokeRole = async (grant) => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/role-access/${grant.roleAccessId}`, { method: 'DELETE' });
            const data = await response.json();
            if (data.success) {
                toast.success('Role access revoked');
                setRoleGrants(prev => prev.filter(g => g.roleAccessId !== grant.roleAccessId));
                onAccessChanged?.();
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to revoke role access');
        }
    };

    const handleGrantDept = async (departmentId) => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/department-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ departmentId, permissionLevel: deptPermission, excludedUserIds: null })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Department access granted');
                setDeptSearch('');
                setShowDeptDropdown(false);
                fetchDepartmentGrants();
                onAccessChanged?.();
            } else {
                toast.error(data.message || 'Failed to grant department access');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to grant department access');
        }
    };

    const handleRevokeDept = async (grant) => {
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/department-access/${grant.departmentAccessId}`, { method: 'DELETE' });
            const data = await response.json();
            if (data.success) {
                toast.success('Department access revoked');
                setDepartmentGrants(prev => prev.filter(g => g.departmentAccessId !== grant.departmentAccessId));
                onAccessChanged?.();
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to revoke department access');
        }
    };

    const handleGrantUser = async (user) => {
        try {
            await onGrantAccess({ userId: user.userID, permissionLevel: userPermission });
            toast.success(`Access granted to ${user.firstName} ${user.lastName}`);
            setUserSearch('');
            setShowUserDropdown(false);
        } catch (err) {
            Sentry.captureException(err);
            toast.error(err.message || 'Failed to grant user access');
        }
    };

    const handleRevokeUser = async (access) => {
        try {
            await onRevokeAccess(access.accessId);
            toast.success(`Access revoked from ${access.userName}`);
        } catch (err) {
            Sentry.captureException(err);
            toast.error(err.message || 'Failed to revoke access');
        }
    };

    const handleTransferOwnership = async (newOwner) => {
        setTransferring(true);
        try {
            const response = await apiFetch(`/api/KanbanBoards/${board.boardId}/transfer-ownership`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newOwnerId: newOwner.userID })
            });
            const data = await response.json();
            if (data.success) {
                toast.success(`Ownership transferred to ${newOwner.firstName} ${newOwner.lastName}`);
                setCurrentOwnerId(newOwner.userID);
                setCurrentOwnerName(`${newOwner.firstName} ${newOwner.lastName}`);
                setOwnerSearch('');
                setShowOwnerDropdown(false);
                onAccessChanged?.();
            } else {
                toast.error(data.message || 'Failed to transfer ownership');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to transfer ownership');
        } finally {
            setTransferring(false);
        }
    };

    // ── Filtered lists for dropdowns ───────────────────────────────────────
    const grantedRoleIds = roleGrants.map(g => g.roleId);
    const filteredRoles = roles.filter(r =>
        !grantedRoleIds.includes(r.roleID) &&
        (!roleSearch || r.roleName.toLowerCase().includes(roleSearch.toLowerCase()))
    );

    const grantedDeptIds = departmentGrants.map(g => g.departmentId);
    const filteredDepts = departments.filter(d =>
        !grantedDeptIds.includes(d.departmentID) &&
        (!deptSearch || d.departmentName.toLowerCase().includes(deptSearch.toLowerCase()))
    );

    const grantedUserIds = sharedWith.map(a => a.userId);
    const filteredUsers = allUsers.filter(u => {
        if (u.userID === board.ownerId) return false;
        if (grantedUserIds.includes(u.userID)) return false;
        if (!userSearch) return true;
        const q = userSearch.toLowerCase();
        return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || (u.userEmail || '').toLowerCase().includes(q);
    }).slice(0, 20);

    const filteredOwnerUsers = allUsers.filter(u => {
        if (u.userID === currentOwnerId) return false;
        if (!ownerSearch) return false; // only show results when searching
        const q = ownerSearch.toLowerCase();
        return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || (u.userEmail || '').toLowerCase().includes(q);
    }).slice(0, 20);

    // The ownership-transfer section is admin-only on both sides: the backend
    // TransferBoardOwnership endpoint enforces IsAdminUser() (JWT role = "Admin"),
    // so the client must match that check exactly.  Using hasPermission alone is
    // insufficient because the '/admin/action-items' permission can be granted
    // without the Admin JWT role, which would show the control but always 403.
    const isAdmin = user?.roleName?.toLowerCase() === 'admin';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Manage Board Access</h2>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Board Info */}
                    <div style={styles.boardHeader}>
                        {board.icon && <span style={{ fontSize: '1.4rem' }}><FaIcon icon={board.icon} /></span>}
                        <div>
                            <h3 style={styles.boardName}>{board.name}</h3>
                            {board.description && <p style={styles.boardDesc}>{board.description}</p>}
                        </div>
                    </div>

                    {/* ── Board Owner (admin only) ───────────────────── */}
                    {isAdmin && (
                        <div style={{ ...styles.formField, marginBottom: '20px', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <label style={styles.label}><IoSwapHorizontal size={16} /> Board Owner</label>
                            <small style={styles.helpText}>The owner has full admin access to this board. Transfer ownership to another active user.</small>

                            {/* Current owner chip */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Current owner:</span>
                                <span style={{ ...styles.tag, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', fontSize: '0.82rem' }}>
                                    {currentOwnerName}
                                </span>
                            </div>

                            {/* Owner search */}
                            <div style={styles.searchableSelect} ref={ownerSearchRef}>
                                <IoSearch size={14} style={styles.searchInputIcon} />
                                <input
                                    style={styles.searchInput}
                                    placeholder="Search users to transfer ownership..."
                                    value={ownerSearch}
                                    onChange={e => { setOwnerSearch(e.target.value); setShowOwnerDropdown(true); }}
                                    onFocus={() => setShowOwnerDropdown(true)}
                                    disabled={transferring}
                                />
                                {showOwnerDropdown && ownerSearch && (
                                    <div style={styles.dropdown}>
                                        {filteredOwnerUsers.length === 0 ? (
                                            <div style={styles.dropdownEmpty}>No matching users</div>
                                        ) : filteredOwnerUsers.map(u => (
                                            <div
                                                key={u.userID}
                                                style={styles.dropdownItem}
                                                onClick={() => handleTransferOwnership(u)}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                            >
                                                <span>{u.firstName} {u.lastName}</span>
                                                {u.userEmail && <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{u.userEmail}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Allowed Roles ──────────────────────────────── */}
                    <div style={styles.formField}>
                        <label style={styles.label}><IoPeople size={16} /> Allowed Roles</label>
                        <small style={styles.helpText}>Search and select roles to grant board access.</small>

                        {/* Permission selector for new role grants */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Permission for new grants:</span>
                            <select style={styles.permSelect} value={rolePermission} onChange={e => setRolePermission(e.target.value)}>
                                {PERMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>

                        {/* Current role tags */}
                        {roleGrants.length > 0 && (
                            <div style={styles.selectedTags}>
                                {roleGrants.map(grant => (
                                    <span key={grant.roleAccessId} style={{ ...styles.tag, ...styles.tagRole }}>
                                        {grant.roleName}
                                        <span style={styles.permBadge}>{grant.permissionLevel}</span>
                                        <button type="button" style={styles.tagRemove} onClick={() => handleRevokeRole(grant)} title="Remove">
                                            <IoClose size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Search dropdown */}
                        <div style={styles.searchableSelect} ref={roleSearchRef}>
                            <IoSearch size={14} style={styles.searchInputIcon} />
                            <input
                                style={styles.searchInput}
                                placeholder="Search roles..."
                                value={roleSearch}
                                onChange={e => { setRoleSearch(e.target.value); setShowRoleDropdown(true); }}
                                onFocus={() => setShowRoleDropdown(true)}
                            />
                            {showRoleDropdown && (
                                <div style={styles.dropdown}>
                                    {filteredRoles.length === 0 ? (
                                        <div style={styles.dropdownEmpty}>{roleSearch ? 'No matching roles' : 'All roles granted'}</div>
                                    ) : filteredRoles.map(r => (
                                        <div
                                            key={r.roleID}
                                            style={styles.dropdownItem}
                                            onClick={() => handleGrantRole(r.roleID)}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            {r.roleName}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Allowed Departments ───────────────────────── */}
                    <div style={{ ...styles.formField, ...styles.sectionDivider }}>
                        <label style={styles.label}><IoBusiness size={16} /> Allowed Departments</label>
                        <small style={styles.helpText}>Search and select departments to grant board access to all members.</small>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Permission for new grants:</span>
                            <select style={styles.permSelect} value={deptPermission} onChange={e => setDeptPermission(e.target.value)}>
                                {PERMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>

                        {departmentGrants.length > 0 && (
                            <div style={styles.selectedTags}>
                                {departmentGrants.map(grant => (
                                    <span key={grant.departmentAccessId} style={{ ...styles.tag, ...styles.tagDept }}>
                                        {grant.departmentName}
                                        <span style={styles.permBadge}>{grant.permissionLevel}</span>
                                        <button type="button" style={styles.tagRemove} onClick={() => handleRevokeDept(grant)} title="Remove">
                                            <IoClose size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div style={styles.searchableSelect} ref={deptSearchRef}>
                            <IoSearch size={14} style={styles.searchInputIcon} />
                            <input
                                style={styles.searchInput}
                                placeholder="Search departments..."
                                value={deptSearch}
                                onChange={e => { setDeptSearch(e.target.value); setShowDeptDropdown(true); }}
                                onFocus={() => setShowDeptDropdown(true)}
                            />
                            {showDeptDropdown && (
                                <div style={styles.dropdown}>
                                    {filteredDepts.length === 0 ? (
                                        <div style={styles.dropdownEmpty}>{deptSearch ? 'No matching departments' : 'All departments granted'}</div>
                                    ) : filteredDepts.map(d => (
                                        <div
                                            key={d.departmentID}
                                            style={styles.dropdownItem}
                                            onClick={() => handleGrantDept(d.departmentID)}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            {d.departmentName}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Allowed Users ──────────────────────────────── */}
                    <div style={{ ...styles.formField, ...styles.sectionDivider }}>
                        <label style={styles.label}><IoPerson size={16} /> Allowed Users</label>
                        <small style={styles.helpText}>Search and select users to grant specific access (in addition to role/department scope).</small>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Permission for new grants:</span>
                            <select style={styles.permSelect} value={userPermission} onChange={e => setUserPermission(e.target.value)}>
                                {PERMISSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>

                        {sharedWith.length > 0 && (
                            <div style={styles.selectedTags}>
                                {sharedWith.map(access => (
                                    <span key={access.accessId} style={{ ...styles.tag, ...styles.tagUser }}>
                                        {access.userName}
                                        <span style={styles.permBadge}>{access.permissionLevel}</span>
                                        <button type="button" style={styles.tagRemove} onClick={() => handleRevokeUser(access)} title="Remove">
                                            <IoClose size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div style={styles.searchableSelect} ref={userSearchRef}>
                            <IoSearch size={14} style={styles.searchInputIcon} />
                            <input
                                style={styles.searchInput}
                                placeholder="Search users by name or email..."
                                value={userSearch}
                                onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                                onFocus={() => setShowUserDropdown(true)}
                            />
                            {showUserDropdown && (
                                <div style={styles.dropdown}>
                                    {filteredUsers.length === 0 ? (
                                        <div style={styles.dropdownEmpty}>{userSearch ? 'No matching users' : 'All users granted'}</div>
                                    ) : filteredUsers.map(u => (
                                        <div
                                            key={u.userID}
                                            style={styles.dropdownItem}
                                            onClick={() => handleGrantUser(u)}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                        >
                                            <span>{u.firstName} {u.lastName}</span>
                                            {u.userEmail && <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{u.userEmail}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Board access note */}
                    <div style={{ marginTop: '16px', padding: '10px 14px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                        <small style={{ color: '#1e40af' }}>
                            <IoShieldCheckmark size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Board owners and Admin users always have full access regardless of these grants.
                        </small>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ManageAccessModal;
